import {
  type FC, Fragment, useCallback,
} from 'react';
import { noop } from '~/_shared/utils/function.helpers';
import { useUserIdSelector } from '~/store/userData/userData.selectors';
import { type MapListingItem } from '../item/mapListingItem';
import { type ChangePrivacyLevelProps } from '../useMapListingItems';
import {
  type MapListingRowItem, type MapPrivacyClickParams, MapRowComponent,
} from './mapRow/mapRow.component';

type TableDataProps = Readonly<{
  data: ReadonlyArray<MapListingItem>;
  currentUserId: number | null;
  onMapPrivacyClick: (data: MapPrivacyClickParams) => void;
  onMapSelectToggle: (mapId: number) => void;
  toggledMapIds: ReadonlyArray<number>;
  onShowSubMapsToggle: (mapId: number) => void;
  onSubMapSelectToggle: (params: Readonly<{ mapId: number; subMapId: number }>) => void;
  selectedMapsIds: ReadonlyArray<number>;
  selectedSubMapIds: ReadonlySet<number>;
  width: number;
  onShareSuccess: (props: ChangePrivacyLevelProps) => void;
}>;

export const TableDataComponent: FC<TableDataProps> = (props) => {
  const currentUserId = useUserIdSelector();

  const getOnSubMapSelectToggle = useCallback((mapId: number, subMapId: number) => () => {
    const onSubMapSelectToggle = props.onSubMapSelectToggle;
    return onSubMapSelectToggle({ mapId, subMapId });
  }, [props.onSubMapSelectToggle]);

  return (
    <tbody>
      {props.currentUserId && props.data.map((item, index) => {
        const isMapSelected = props.selectedMapsIds.includes(item.id);
        const areSubMapsExpanded = props.toggledMapIds.includes(item.id);

        return (
          <Fragment key={item.id}>
            <MapRowComponent
              areSubMapsExpanded={areSubMapsExpanded}
              index={index}
              isMapSelected={isMapSelected}
              isOwner={item?.ownerId === props.currentUserId}
              item={item}
              onMapPrivacyClick={props.onMapPrivacyClick}
              onMapSelectToggle={props.onMapSelectToggle}
              onShowSubMapsToggle={props.onShowSubMapsToggle}
              width={props.width}
              onShareSuccess={props.onShareSuccess}
            />

            {areSubMapsExpanded && item.layering?.connected && item.layering?.baseMaps.map((baseMap, subIndex) => {
              const hasAccess = baseMap.sharedUsers.some(user => user.id === currentUserId);
              const baseMapItem: MapListingRowItem | undefined = {
                ...baseMap,
                isBaseLayer: true,
                noAccess: !hasAccess,
                snapshots: [],
              };

              return (
                <MapRowComponent
                  areSubMapsExpanded={false}
                  index={subIndex}
                  isMapSelected={props.selectedSubMapIds.has(baseMap.id)}
                  isOwner={baseMap.ownerId === props.currentUserId}
                  item={baseMapItem}
                  key={item.id + 'sub_map' + subIndex}
                  onMapPrivacyClick={props.onMapPrivacyClick}
                  onMapSelectToggle={getOnSubMapSelectToggle(item.id, baseMap.id)}
                  onShowSubMapsToggle={noop}
                  parentMapItem={item}
                  width={props.width}
                  onShareSuccess={props.onShareSuccess}
                />
              );
            })}
            {areSubMapsExpanded && item.snapshots.map((snapshot, subIndex) => (
              <MapRowComponent
                areSubMapsExpanded={false}
                index={subIndex}
                isMapSelected={props.selectedSubMapIds.has(snapshot.id)}
                isOwner={snapshot.ownerId === props.currentUserId}
                item={snapshot}
                key={item.id + 'sub_map' + subIndex}
                onMapPrivacyClick={props.onMapPrivacyClick}
                onMapSelectToggle={getOnSubMapSelectToggle(item.id, snapshot.id)}
                onShowSubMapsToggle={noop}
                parentMapItem={item}
                width={props.width}
                onShareSuccess={props.onShareSuccess}
              />
            ))}
          </Fragment>
        );
      })}
    </tbody>
  );
};
